import React, { useEffect, useMemo, useRef, useState } from "react";

const BRAND = "#2563eb";

const styles = {
  launcher: {
    position: "fixed",
    right: 16,
    bottom: 16,
    width: 56,
    height: 56,
    borderRadius: 999,
    border: "none",
    cursor: "pointer",
    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
    background: BRAND,
    color: "white",
    fontSize: 22,
    zIndex: 9999,
    touchAction: "manipulation",
  },

  // ✅ overlay only for mobile sheet
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2, 6, 23, 0.45)",
    zIndex: 9998,
  },

  panelBase: {
    position: "fixed",
    zIndex: 9999,
    borderRadius: 16,
    boxShadow: "0 12px 35px rgba(0,0,0,0.25)",
    overflow: "hidden",
    background: "white",
    display: "flex",
    flexDirection: "column",
    border: "1px solid rgba(0,0,0,0.08)",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial",
  },

  // Desktop/tablet floating panel
  panelDesktop: {
    right: 16,
    bottom: 86,
    width: 360,
    height: 480,
  },

  // Mobile bottom sheet
  panelMobile: {
    left: 12,
    right: 12,
    bottom: 12,
    width: "auto",
    height: "auto",
    maxHeight: "calc(100dvh - 24px)",
    borderRadius: 18,
  },

  header: {
    background: BRAND,
    color: "white",
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitle: { fontWeight: 800, fontSize: 14, letterSpacing: 0.2 },
  headerSub: { fontSize: 12, opacity: 0.9 },

  closeBtn: {
    border: "none",
    background: "transparent",
    color: "white",
    fontSize: 18,
    cursor: "pointer",
    lineHeight: 1,
    padding: 6,
    borderRadius: 10,
  },

  body: {
    flex: 1,
    padding: 12,
    overflowY: "auto",
    background: "#f8fafc",
  },

  bubbleRow: { display: "flex", marginBottom: 10 },
  bubble: {
    maxWidth: "86%",
    padding: "10px 12px",
    borderRadius: 14,
    fontSize: 13,
    lineHeight: 1.35,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  userBubble: { marginLeft: "auto", background: BRAND, color: "white" },
  botBubble: { marginRight: "auto", background: "white", color: "#0f172a" },

  hint: {
    fontSize: 11,
    color: "#64748b",
    padding: "0 12px 10px",
    background: "white",
    borderTop: "1px solid rgba(0,0,0,0.06)",
  },

  footer: {
    padding: 10,
    background: "white",
    borderTop: "1px solid rgba(0,0,0,0.08)",
    display: "flex",
    gap: 8,
    alignItems: "center",
    // ✅ handles iPhone bottom safe-area
    paddingBottom: "calc(10px + env(safe-area-inset-bottom, 0px))",
  },

  input: {
    flex: 1,
    minWidth: 0,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.15)",
    outline: "none",
    fontSize: 14,
  },

  send: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "none",
    background: "#0f172a",
    color: "white",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
    touchAction: "manipulation",
  },

  sendDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
};

function normalize(text) {
  return (text || "").toLowerCase().trim();
}

function botReply(userText) {
  const t = normalize(userText);

  if (/(hi|hello|hey|hii|helo|namaste)/.test(t)) {
    return "👋 Hi! How can I help you?\n1) Pricing\n2) Services\n3) Contact";
  }
  if (/(price|pricing|cost|plan)/.test(t)) {
    return "For pricing, which service would you like details for? (Website / App / SEO)";
  }
  if (/(service|services|kya karte|offer)/.test(t)) {
    return "Which service do you need?";
  }
  if (/(contact|call|phone|number|whatsapp|support)/.test(t)) {
    return "For contact, please share your name and requirements. I’ll guide you through the next steps.";
  }
  if (/(time|timing|kab|hours|open)/.test(t)) {
    return "Our working hours are Monday through Saturday, 10:00 AM to 7:00 PM. Please share a convenient time to connect.";
  }

  return "Could you please share a bit more detail about what you need help with (services, pricing, contact, or support)?";
}

function useIsMobile(breakpoint = 600) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

export default function ChatBotWidget() {
  const isMobile = useIsMobile(600);

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState(() => [
    { role: "bot", text: "👋 How can I help you?" },
  ]);

  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  // optional auto-open
  useEffect(() => {
    const timer = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(timer);
  }, []);

  // close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // auto-scroll
  useEffect(() => {
    if (!bodyRef.current) return;
    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open]);

  // focus input when open (helps mobile UX)
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, [open]);

  const canSend = useMemo(() => normalize(input).length > 0, [input]);

  const sendMessage = () => {
    if (!canSend) return;

    const userText = input;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: userText }]);

    setTimeout(() => {
      const reply = botReply(userText);
      setMessages((prev) => [...prev, { role: "bot", text: reply }]);
    }, 350);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const panelStyle = {
    ...styles.panelBase,
    ...(isMobile ? styles.panelMobile : styles.panelDesktop),
  };

  return (
    <>
      {/* ✅ Mobile overlay (tap outside to close) */}
      {open && isMobile && (
        <div
          style={styles.overlay}
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {open && (
        <div style={panelStyle} role="dialog" aria-label="Support Chatbot">
          <div style={styles.header}>
            <div>
              <div style={styles.headerTitle}>Niya</div>
              <div style={styles.headerSub}>Online • Replies instantly</div>
            </div>

            <button
              style={styles.closeBtn}
              onClick={() => setOpen(false)}
              aria-label="Close"
              title="Close"
            >
              ✕
            </button>
          </div>

          <div style={styles.body} ref={bodyRef}>
            {messages.map((m, idx) => (
              <div key={idx} style={styles.bubbleRow}>
                <div
                  style={{
                    ...styles.bubble,
                    ...(m.role === "user" ? styles.userBubble : styles.botBubble),
                  }}
                >
                  {m.text}
                </div>
              </div>
            ))}
          </div>

          <div style={styles.hint}>Tip: try “pricing”, “services”, “contact”.</div>

          <div style={styles.footer}>
            <input
              ref={inputRef}
              style={styles.input}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type your message..."
              inputMode="text"
              autoComplete="off"
            />

            <button
              style={{
                ...styles.send,
                ...(!canSend ? styles.sendDisabled : null),
              }}
              onClick={sendMessage}
              disabled={!canSend}
            >
              Send
            </button>
          </div>
        </div>
      )}

      <button
        style={styles.launcher}
        onClick={() => setOpen((v) => !v)}
        aria-label="Open chat"
        title="Chat"
      >
        💬
      </button>
    </>
  );
}
